import { useState, useEffect } from "react";
import { AppSidebar } from "./AppSidebar";
import { AppHeader } from "./AppHeader";
import { DynamicLogo } from "./DynamicLogo";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Menu, Search, LogOut, User, Bell, X, Settings, LayoutDashboard, Users, Package, Wallet, Wrench, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { useNotifications } from "@/hooks/useNotifications";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { PageSummary } from "./PageSummary";
import { WorkspaceSelector } from "./WorkspaceSelector";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useInactivityLogout } from "@/hooks/useInactivityLogout";
import { useEffect as useNoIndexEffect } from "react";

const SIDEBAR_STORAGE_KEY = "sidebar-collapsed";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface Profile {
  full_name: string | null;
  avatar_url: string | null;
  role: string | null;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored === "true";
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [profile, setProfile] = useState<Profile | null>(null);

  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  useInactivityLogout();

  // Block search engine indexing for all backend pages
  useNoIndexEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');
    return () => {
      meta?.remove();
    };
  }, []);
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const { isSuperAdmin } = useSuperAdmin();
  const { currentPlan } = useWorkspace();

  // Allow text selection and screen capture for superadmin
  useEffect(() => {
    // Removed local superadmin override to centralize logic in GlobalProtection
    // This avoids race conditions between different hooks managing the same class
    return () => {};
  }, [isSuperAdmin]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Use user.id instead of user object to prevent unnecessary re-fetches
  const userId = user?.id;
  
  useEffect(() => {
    if (userId) {
      fetchProfile();
    }
  }, [userId]);

  const fetchProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("profiles")
      .select("full_name, avatar_url, role")
      .eq("user_id", user.id)
      .maybeSingle();
    if (data) {
      setProfile(data);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const displayName = profile?.full_name || user?.email?.split("@")[0] || "Usuario";
  const initials = displayName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-background">
      <AppSidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileMenuOpen}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      
      {/* Mobile header with menu button, search and profile */}
      <div className="fixed top-0 left-0 right-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background px-3 md:hidden">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(true)}
            className="h-9 w-9"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/" className="ml-2">
            <DynamicLogo 
              width={28} 
              height={28} 
              type="dashboard"
              showText
              textClassName="text-sm"
            />
          </Link>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Mobile Search Button */}
          <Drawer open={searchOpen} onOpenChange={setSearchOpen}>
            <DrawerTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground">
                <Search className="h-5 w-5" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="px-4 pb-6">
              <DrawerHeader className="flex items-center justify-between px-0">
                <DrawerTitle>Buscar</DrawerTitle>
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon">
                    <X className="h-4 w-4" />
                  </Button>
                </DrawerClose>
              </DrawerHeader>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar en el sistema..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 bg-secondary border-border"
                  autoFocus
                />
              </div>
            </DrawerContent>
          </Drawer>

          {/* Theme Toggle removed from mobile header per user request */}
          <div data-testid="mobile-header-theme-toggle" className="hidden" />

          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 relative text-muted-foreground"
            onClick={() => navigate('/notifications')}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge className="absolute -right-1 -top-1 h-4 w-4 rounded-full p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-[10px]">
                {unreadCount}
              </Badge>
            )}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary">{initials}</span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover border-border">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Acceso rápido
              </DropdownMenuLabel>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/')}>
                <LayoutDashboard className="mr-2 h-4 w-4" />
                <span>Dashboard</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/crm/clients')}>
                <Users className="mr-2 h-4 w-4" />
                <span>Clientes</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/erp/products')}>
                <Package className="mr-2 h-4 w-4" />
                <span>Productos</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/finance/invoices')}>
                <Wallet className="mr-2 h-4 w-4" />
                <span>Facturas</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/workshop/orders')}>
                <Wrench className="mr-2 h-4 w-4" />
                <span>Órdenes de trabajo</span>
              </DropdownMenuItem>
              {(currentPlan === "enterprise" || isSuperAdmin) && (
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/insurance')}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  <span>Seguros</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings/general')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Configuración</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="cursor-pointer text-destructive focus:text-destructive"
                onClick={handleSignOut}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Cerrar sesión</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div
        className={cn(
          "transition-all duration-300",
          "pt-14 md:pt-0",
          sidebarCollapsed ? "md:ml-16" : "md:ml-64"
        )}
      >
        <div className="hidden md:block">
          <AppHeader />
        </div>
        <main className="p-4 md:p-6">
          {children}
          <PageSummary />
        </main>
      </div>
    </div>
  );
}